import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';

import { selectCustomerGroupId } from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import { RouterLinkWithHrefDirective } from '@amfam/shared/ui/ds-router-link/lib/router-link.directive';
import { MAJOR_PARTNER_IDS } from '@amfam/shared/utility/brand/lib/services/partner-mappings';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { ContentDirective } from '@amfam/shared/utility/shared-services/lib/copy/content.directive';

import { HeaderNavRect } from '../../../models/header-data.model';
import { NotificationObj } from '../../../models/notification-model';
import { HeaderWrapperService } from '../../../services/header-wrapper.service';

@Component({
  selector: 'ds-header-notification',
  templateUrl: './ds-header-notification.component.html',
  styleUrls: ['../shared/ds-header-navigation-links.scss'],
  host: {
    '(window:resize)': 'resize()',
    '(keyup.esc)': 'onEsc()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLinkWithHrefDirective, ContentDirective]
})
export class DsHeaderNotificationComponent {
  private router = inject(Router);
  private headerWrapperService = inject(HeaderWrapperService);
  private renderer = inject(Renderer2);
  private featureFlagService = inject(FeatureFlagService);
  private store = inject(Store);

  readonly notifications = input<NotificationObj[] | null>();
  readonly showNotificationMenuSub = signal('hide');
  readonly headerNavRect = input.required<HeaderNavRect | undefined | null>();
  readonly experienceId = input<string>();
  readonly subOpenEvent = output<DsHeaderNotificationComponent>();
  readonly notificationDismissClickEvent = output();
  readonly notificationElem = viewChild<ElementRef>('notificationElem');

  readonly subNotificationElem = viewChild<ElementRef>('subNotificationElem');

  private readonly customerGroupId = this.store.selectSignal(selectCustomerGroupId);
  readonly notificationMenu = computed(() => {
    const notifications = this.notifications();
    return notifications
      ? this.headerWrapperService.buildNotificationMessageArray(
          notifications,
          MAJOR_PARTNER_IDS.includes(this.customerGroupId()) ? undefined : this.experienceId()
        )
      : undefined;
  });
  private readonly messageCount = computed(
    () => this.notificationMenu()?.messagesArray?.length ?? 0
  );
  readonly notificationMessage = computed(() => {
    const messageCount = this.messageCount();
    if (messageCount === 1) {
      return 'You have a new notification';
    } else if (messageCount >= 2) {
      return 'You have ' + messageCount + ' new notifications';
    }
    return 'You have no notifications';
  });
  readonly submenuExpanded = signal('false');
  readonly submenuHasPopup = computed(() => this.messageCount() >= 1);
  readonly queryParams: Record<string, string>;

  constructor() {
    this.queryParams = this.featureFlagService.isEnabled('policy_new_template')
      ? { scrollTo: 'documentsCard' }
      : { openAccordion: 'documents', tid: 'newdocnotice' };

    // Once the menu for a new set of notifications is rendered, center it under the bell.
    afterRenderEffect(() => {
      this.notificationMenu();
      untracked(() => {
        if (this.messageCount() >= 1) {
          this.headerWrapperService.setLoaded(true);
        }
        this.centerSubNav();
      });
    });

    // Re-center the sub menu under the bell once each navigation completes.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.centerSubNav());
  }

  resize() {
    // Re-run sub menu centering if window resize occurs
    this.centerSubNav();
  }

  // ESC has to close menu for WCAG 2.0
  onEsc() {
    this.showNotificationMenuSub.set('hide');
    this.submenuExpanded.set('false');
  }

  onDismissClick(message: NotificationObj) {
    this.headerWrapperService.setNotificationToDismiss(message);
    // Recenter roofline mover as the notification menu may have been removed.
    if (this.messageCount() <= 1) {
      this.headerWrapperService.resetRooflineMover();
    }
  }

  subOpen() {
    if (this.showNotificationMenuSub() !== 'show') {
      // Hide other nav items / sub menus
      this.subOpenEvent.emit(this);
      // Show this sub menu
      this.showNotificationMenuSub.set('show');
      if (this.submenuHasPopup() && this.submenuExpanded() === 'false') {
        this.submenuExpanded.set('true');
      }
    }
    this.setLinkFocus(this.notificationElem());
  }
  subClose() {
    // Hide this sub menu
    if (this.showNotificationMenuSub() !== 'hide') {
      this.showNotificationMenuSub.set('hide');
      if (this.submenuExpanded() === 'true') {
        this.submenuExpanded.set('false');
      }
    }
    this.setLinkBlur(this.notificationElem());
  }

  private setLinkFocus(linkElem: ElementRef | undefined) {
    if (!linkElem) {
      return;
    }
    this.renderer.addClass(linkElem.nativeElement, 'focus');
    this.headerWrapperService.setLinkFocus(linkElem);
  }

  private setLinkBlur(linkElem: ElementRef | undefined) {
    if (linkElem) {
      this.renderer.removeClass(linkElem.nativeElement, 'focus');
    }
    this.headerWrapperService.setLinkFocus(null);
  }

  private centerSubNav() {
    this.headerWrapperService.centerSubNav(
      this.notificationElem(),
      this.subNotificationElem(),
      this.headerNavRect()
    );
  }
}
